<template>
  <div class="print-page-wrap">

    <!-- Screen toolbar (hidden when printing) -->
    <div class="print-toolbar no-print">
      <button class="toolbar-btn" @click="$router.go(-1)">
        <font-awesome-icon icon="arrow-left" class="me-1" /> Back
      </button>
      <div class="toolbar-right">
        <button class="toolbar-btn toolbar-btn--primary" @click="printDoc">
          <font-awesome-icon icon="print" class="me-1" /> Print
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="print-loading no-print">
      <div class="spinner"></div>
      <p>Loading goods receipt note…</p>
    </div>

    <!-- Document -->
    <div v-else-if="grn" class="document" id="printMe">

      <!-- Document header -->
      <div class="doc-header">
        <div class="doc-brand">
          <img v-if="business.logo" :src="hostName + business.logo" class="doc-logo" alt="Logo" />
          <div v-else class="doc-logo-placeholder">{{ (business.name || '?').charAt(0) }}</div>
          <div class="doc-brand-text">
            <div class="doc-company-name">{{ business.name || '—' }}</div>
            <div class="doc-company-sub" v-if="business.address">{{ business.address }}</div>
            <div class="doc-company-sub" v-if="business.email">{{ business.email }}</div>
          </div>
        </div>
        <div class="doc-title-block">
          <div class="doc-title">GOODS RECEIPT NOTE</div>
          <table class="doc-meta-table">
            <tr>
              <td class="dmt-label">GRN Number</td>
              <td class="dmt-value mono">{{ grn.grn_number }}</td>
            </tr>
            <tr>
              <td class="dmt-label">Date Received</td>
              <td class="dmt-value">{{ formatDate(grn.received_at) }}</td>
            </tr>
            <tr v-if="grn.po">
              <td class="dmt-label">Purchase Order</td>
              <td class="dmt-value mono-sm">{{ grn.po.po_number }}</td>
            </tr>
          </table>
        </div>
      </div>

      <div class="doc-divider"></div>

      <!-- Vendor + PO two-column -->
      <div class="doc-two-col">
        <div class="doc-address-block">
          <div class="doc-address-head">VENDOR</div>
          <div class="doc-address-name">{{ grn.po?.vendor?.name || '—' }}</div>
          <div class="doc-address-line" v-if="grn.po?.vendor?.primaryContact?.email">
            {{ grn.po.vendor.primaryContact.email }}
          </div>
          <div class="doc-address-line" v-if="grn.po?.vendor?.primaryContact?.phone">
            {{ grn.po.vendor.primaryContact.phone }}
          </div>
        </div>
        <div class="doc-address-block" v-if="grn.po">
          <div class="doc-address-head">PURCHASE ORDER</div>
          <div class="doc-address-name mono">{{ grn.po.po_number }}</div>
          <div class="doc-address-line" v-if="grn.po.title">{{ grn.po.title }}</div>
          <div class="doc-address-line" v-if="grn.po.currency">Currency: {{ grn.po.currency }}</div>
        </div>
        <div class="doc-address-block" v-if="grn.notes">
          <div class="doc-address-head">NOTES</div>
          <div class="doc-address-line" style="white-space:pre-line">{{ grn.notes }}</div>
        </div>
      </div>

      <div class="doc-divider doc-divider--dashed"></div>

      <!-- Line items -->
      <div class="table-section-head">
        <span class="table-section-title">Items Received</span>
        <span class="table-count-badge">{{ grn.lines?.length || 0 }} item{{ (grn.lines?.length || 0) !== 1 ? 's' : '' }}</span>
      </div>
      <div class="doc-table-wrap">
        <table class="doc-table">
          <colgroup>
            <col class="col-no" />
            <col class="col-desc" />
            <col class="col-product" />
            <col class="col-location" />
            <col class="col-qty" />
            <col class="col-uom" />
          </colgroup>
          <thead>
            <tr>
              <th>#</th>
              <th>Description</th>
              <th>Product</th>
              <th>Location / Store</th>
              <th class="th-r">Qty Received</th>
              <th>UOM</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(line, i) in grn.lines" :key="line.id" :class="i % 2 === 1 ? 'row-alt' : ''">
              <td class="td-idx">{{ i + 1 }}</td>
              <td class="td-desc-cell">
                <div class="td-desc">{{ line.description || '—' }}</div>
              </td>
              <td class="td-product">
                <span v-if="line.product" class="td-product-name">{{ line.product.product_name }}</span>
                <span v-else class="dim">—</span>
              </td>
              <td class="td-location">
                <span v-if="line.location">{{ line.location.store_name }}</span>
                <span v-else class="dim">—</span>
              </td>
              <td class="td-r td-qty">{{ line.quantity_received }}</td>
              <td class="td-uom">{{ line.uom || '—' }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Signature block -->
      <div class="doc-divider doc-divider--dashed"></div>
      <div class="doc-sig-block">
        <div class="doc-sig-name" v-if="receivedByName">{{ receivedByName }}</div>
        <div class="doc-sig-line"></div>
        <div class="doc-sig-label">Received by</div>
        <div class="doc-sig-sub">{{ formatDate(grn.received_at) }}</div>
      </div>

      <!-- Footer -->
      <div class="doc-footer">
        Generated {{ formatDate(new Date().toISOString()) }} · {{ grn.grn_number }} · {{ business.name }}
      </div>

    </div>

    <div v-else class="print-loading no-print">
      <p>Goods receipt note not found.</p>
    </div>

  </div>
</template>

<script>
import axios from 'axios';
import url from '../../../main';
import { useToast } from 'vue-toastification';

export default {
  name: 'PrintGRN',
  setup() { return { toast: useToast() }; },

  data() {
    return {
      grn:      null,
      business: {},
      loading:  true,
      hostName: url.hostName,
    };
  },

  computed: {
    receivedByName() {
      const u = this.grn?.received_by_user;
      if (!u) return '';
      return [u.first_name, u.last_name].filter(Boolean).join(' ');
    },
  },

  mounted() {
    Promise.all([this.fetchGrn(), this.fetchBusiness()])
      .finally(() => { this.loading = false; });
  },

  methods: {
    async fetchGrn() {
      try {
        const { data } = await axios.get(`/procurement/grns/${this.$route.params.id}`);
        this.grn = data.data;
      } catch {
        this.toast.error('Failed to load goods receipt note');
      }
    },

    async fetchBusiness() {
      try {
        const { data } = await axios.get('/admin/business/user/active-business');
        this.business = data.data ?? {};
      } catch { /* non-critical */ }
    },

    printDoc() {
      const style = document.createElement('style');
      style.id = '__grn-print-style';
      style.innerHTML = `
        @page { size: A4; margin: 14mm; }
        @media print {
          body > * { visibility: hidden !important; }
          #printMe, #printMe * { visibility: visible !important; }
          #printMe { position: absolute; left: 0; top: 0; width: 100%; background: white; }
        }
      `;
      document.head.appendChild(style);
      window.print();
      setTimeout(() => document.getElementById('__grn-print-style')?.remove(), 1200);
    },

    formatDate(d) {
      if (!d) return '—';
      return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    },
  },
};
</script>

<style scoped>
/* ── Page shell ───────────────────────────────────────────────────────────── */
.print-page-wrap { background: #f0f4f8; min-height: 100vh; padding: 20px; }

.print-toolbar {
  display: flex; align-items: center; justify-content: space-between;
  max-width: 860px; margin: 0 auto 20px; gap: 10px;
}
.toolbar-right { display: flex; gap: 10px; }
.toolbar-btn {
  display: inline-flex; align-items: center; gap: 6px;
  background: #fff; color: #1a2b45; border: 1.5px solid #dde5f0;
  font-size: 13px; font-weight: 600; padding: 9px 18px; border-radius: 8px; cursor: pointer;
}
.toolbar-btn:hover { background: #f4f7fb; }
.toolbar-btn--primary { background: #004AAD; color: #fff; border-color: #004AAD; }
.toolbar-btn--primary:hover { background: #003a8c; }

.print-loading { max-width: 860px; margin: 60px auto; text-align: center; color: #7a90b0; display: flex; flex-direction: column; align-items: center; gap: 14px; }
.spinner { width: 32px; height: 32px; border: 3px solid #e8edf5; border-top-color: #004AAD; border-radius: 50%; animation: spin .7s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

/* ── Document ─────────────────────────────────────────────────────────────── */
.document {
  max-width: 860px; margin: 0 auto;
  background: #fff; border-radius: 12px; padding: 48px 56px;
  box-shadow: 0 4px 24px rgba(0,0,0,.08);
  font-family: 'Segoe UI', system-ui, sans-serif;
  font-size: 13px; color: #1a2b45;
}

/* Header */
.doc-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 24px; }
.doc-brand  { display: flex; align-items: center; gap: 14px; }
.doc-logo   { height: 56px; width: auto; object-fit: contain; }
.doc-logo-placeholder {
  width: 56px; height: 56px; border-radius: 10px; background: #166534;
  color: #fff; font-size: 22px; font-weight: 700;
  display: flex; align-items: center; justify-content: center;
}
.doc-company-name { font-size: 17px; font-weight: 700; color: #1a2b45; }
.doc-company-sub  { font-size: 12px; color: #7a90b0; margin-top: 2px; }

.doc-title-block { text-align: right; }
.doc-title {
  font-size: 22px; font-weight: 800; color: #166534;
  letter-spacing: .04em; text-transform: uppercase; margin-bottom: 12px;
}
.doc-meta-table { margin-left: auto; border-collapse: collapse; }
.dmt-label { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .04em; color: #7a90b0; padding: 3px 10px 3px 0; white-space: nowrap; }
.dmt-value { font-size: 13px; color: #1a2b45; font-weight: 600; padding: 3px 0; }

/* Dividers */
.doc-divider         { border: none; border-top: 2px solid #e4eaf2; margin: 24px 0; }
.doc-divider--dashed { border-top-style: dashed; border-color: #c8d5e8; }

/* Two-column info section */
.doc-two-col { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 24px; }
.doc-address-block { display: flex; flex-direction: column; gap: 4px; }
.doc-address-head { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .07em; color: #aab8cc; margin-bottom: 6px; }
.doc-address-name { font-size: 14px; font-weight: 700; color: #1a2b45; }
.doc-address-line { font-size: 12.5px; color: #4b5c73; }

/* Section label */
.table-section-head { display: flex; align-items: center; gap: 10px; margin-bottom: 10px; }
.table-section-title { font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: .07em; color: #1a2b45; }
.table-count-badge { background: #f0fdf4; color: #166534; font-size: 11px; font-weight: 600; border-radius: 20px; padding: 2px 9px; }

/* Table */
.doc-table-wrap { border: 1px solid #d5dfe9; border-radius: 10px; overflow: hidden; }
.doc-table { width: 100%; border-collapse: collapse; table-layout: fixed; font-size: 12.5px; }

.col-no       { width: 4%; }
.col-desc     { width: 30%; }
.col-product  { width: 22%; }
.col-location { width: 20%; }
.col-qty      { width: 12%; }
.col-uom      { width: 12%; }

.doc-table thead tr { background: #1a2b45; }
.doc-table th {
  padding: 11px 14px; font-size: 10px; font-weight: 700;
  text-transform: uppercase; letter-spacing: .07em;
  color: rgba(255,255,255,.7); text-align: left; border: none;
  overflow: hidden; white-space: nowrap;
}
.th-r { text-align: right; }

.doc-table tbody tr           { background: #fff; }
.doc-table tbody tr.row-alt   { background: #f7f9fc; }
.doc-table tbody tr:not(:last-child) td { border-bottom: 1px solid #e8edf3; }
.doc-table td { padding: 11px 14px; vertical-align: middle; }

.td-idx     { color: #b0bdd0; font-size: 11.5px; font-weight: 700; text-align: center; }
.td-desc-cell { vertical-align: top; padding-top: 12px; }
.td-desc    { font-weight: 600; color: #1a2b45; font-size: 13px; word-break: break-word; line-height: 1.4; }
.td-product-name { font-size: 12.5px; color: #166534; font-weight: 600; }
.td-product { vertical-align: top; padding-top: 12px; }
.td-location { font-size: 12.5px; color: #4b5c73; }
.td-r       { text-align: right; }
.td-qty     { font-size: 13px; font-weight: 700; color: #166534; font-variant-numeric: tabular-nums; }
.td-uom     { font-size: 11.5px; color: #8fa0b5; }

/* Signature block */
.doc-sig-block    { width: 240px; margin-top: 8px; }
.doc-sig-name     { font-size: 13px; font-weight: 700; color: #166534; margin-bottom: 4px; }
.doc-sig-line     { border-bottom: 1.5px solid #1a2b45; margin-bottom: 8px; height: 40px; }
.doc-sig-label    { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .05em; color: #1a2b45; }
.doc-sig-sub      { font-size: 11px; color: #aab8cc; margin-top: 2px; }

.mono    { font-family: monospace; }
.mono-sm { font-family: monospace; font-size: 12px; }
.dim     { color: #aab8cc; }
.me-1    { margin-right: 4px; }

/* Footer */
.doc-footer { margin-top: 32px; text-align: center; font-size: 10.5px; color: #aab8cc; }

/* ── Print overrides ─────────────────────────────────────────────────────── */
@media print {
  .no-print        { display: none !important; }
  .print-page-wrap { background: white; padding: 0; }
  .document        { box-shadow: none; border-radius: 0; padding: 0; max-width: 100%; }
}

@media (max-width: 700px) {
  .document        { padding: 24px 20px; }
  .doc-header      { flex-direction: column; }
  .doc-title-block { text-align: left; }
  .doc-meta-table  { margin-left: 0; }
  .doc-two-col     { grid-template-columns: 1fr; }
  .doc-sig-grid    { grid-template-columns: 1fr; gap: 24px; }
  .doc-table th:nth-child(3), .doc-table td:nth-child(3),
  .doc-table th:nth-child(4), .doc-table td:nth-child(4) { display: none; }
  .col-product, .col-location { display: none; }
  .col-desc { width: 60%; }
  .col-qty  { width: 20%; }
  .col-uom  { width: 20%; }
}
</style>
